import { useEffect, useMemo, useState } from 'react';

import { ExclamationCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd/es/menu';
import { Spin } from 'antd';
import { useSimpleModal } from '../Modal/Simple';
import { PostShort, PostStatus, usePostDelete, usePostUpdate } from '../../../hooks/api/posts';
import { CommentData, useCommentsGet } from '../../../hooks/api/comments';
import PostOrCommentTemplate, { useUniqueData } from '../PostOrCommentTemplate';
import Comment from '../Comment';
import styles from '../PostOrCommentTemplate/index.module.scss';

export interface CommentProps {
  data: PostShort;
  setPage: (page: number) => void;
}

export default function Post({ data, setPage }: CommentProps): JSX.Element {
  const { modal, contextHolder } = useSimpleModal();
  const [open, setOpen] = useState(false);
  const commentsGet = useCommentsGet();
  const postDelete = usePostDelete(data.id);
  const postUpdate = usePostUpdate(data.id);
  const [commentPage, setCommentPage] = useState(1);
  const [commentListAcc] = useUniqueData<CommentData>(commentsGet, commentPage);

  const loadComments = (trigger = false) => {
    if (open && (commentListAcc.list.length === 0 || trigger === true)) {
      commentsGet.fetch({
        postId: data.id,
        page: commentPage,
      });
    }
  };

  useEffect(() => {
    if (postDelete.data && !postDelete.error && !postDelete.loading) {
      setPage(1);
    }
  }, [postDelete.data]);

  useEffect(() => {
    if (postUpdate.data && !postUpdate.error && !postUpdate.loading) {
      setPage(1);
    }
  }, [postUpdate.data]);

  useEffect(loadComments, [open, commentPage]);

  const confirmDelete = () => {
    if (data.id) {
      modal.confirm({
        title: 'Are you sure you want to Delete the post?',
        icon: <ExclamationCircleOutlined />,
        onOk: () => postDelete.fetch(),
        okText: 'Delete',
      });
    }
  };

  const confirmDeactivate = () => {
    modal.confirm({
      title: 'Are you sure you want to Deactivate the post?',
      icon: <ExclamationCircleOutlined />,
      onOk: () => postUpdate.fetch({
        status: PostStatus.DEACTIVATED,
      }),
      okText: 'Deactivate',
    });
  };

  const confirmPublish = () => {
    modal.confirm({
      title: 'Are you sure you want to Publish the post?',
      icon: <ExclamationCircleOutlined />,
      onOk: () => postUpdate.fetch({
        status: PostStatus.PUBLISHED,
      }),
      okText: 'Publish',
    });
  };

  const dropdownMenu: MenuProps = useMemo(
    () => ({
      items: [
        // {
        //   key: '1',
        //   label: 'Edit',
        //   onClick: () => navigate(`/posts/${data.id}/update`),
        // },
        {
          key: '2',
          label: 'Delete',
          onClick: confirmDelete,
        },
        data.status === 'published'
          ? {
            key: '3',
            label: 'Hide',
            onClick: confirmDeactivate,
          }
          : {
            key: '3',
            label: 'Publish',
            onClick: confirmPublish,
          },
        {
          key: '4',
          label: (
            <a href="https://dev.tippingguide.com" target="_blank" rel="noreferrer">
              View on Website
            </a>
          ),
        },
      ],
    }),
    [data],
  );

  const childrenList = useMemo(
    () => [
      ...(commentListAcc.list.map((comment) => (
        <Comment
          setPage={(newPage) => {
            setCommentPage(newPage);
            loadComments(true);
          }}
          key={comment.id}
          data={comment}
        />
      )) || []),
      commentsGet.loading ? (
        <Spin key="loading" indicator={<LoadingOutlined spin />} className={styles.spin} />
      ) : undefined,
    ].filter((value) => value),
    [commentListAcc.list, commentsGet.loading],
  );

  return (
    <>
      <PostOrCommentTemplate
        loading={commentsGet.loading}
        open={open}
        setOpen={setOpen}
        nextPage={() => setPage(commentPage + 1)}
        data={data}
        type="posts"
        dropdownMenu={dropdownMenu}
        showMoreText={commentListAcc.lastMeta?.hasNextPage && !commentsGet.loading ? 'Show more comment' : undefined}
        childrenList={open ? childrenList : undefined}
      />
      {contextHolder}
    </>
  );
}
